'use client'

import { useContext } from 'react'
import type { TypeDuplexImageBlockFields } from '@lib/generated-types'
import { DuplexImageBlock as Block } from '@shc/ui'
import NextImage from '@components/next-image'
import getImageSizes from '@helpers/getImageSizes'
import useAnalytics, { type SPEvent, type SPContext } from '@hooks/use-analytics'
import { BlockContext } from '@lib/analytics'
import { routeMapper } from '@lib/menu-item-mapper'
import { richTextRender } from '@lib/rich-text-base'
import { pyMap } from '@lib/token-maps'
import slugify from 'slugify'

const DuplexImageBlock = ({
  sectionLink,
  heading,
  description,
  image,
  button1Label,
  button1Link,
  button1AnchorOrExternalLink,
  button2Label,
  button2Link,
  button2AnchorOrExternalLink,
  imagePosition,
  backgroundColor,
  paddingTop,
  paddingBottom,
  preheading,
}: TypeDuplexImageBlockFields) => {
  const btnLink1 = routeMapper(button1Link, button1AnchorOrExternalLink)
  const btnLink2 = routeMapper(button2Link, button2AnchorOrExternalLink)
  const imageFields = image.fields.image.fields
  const imageDimensions = imageFields.file.details.image
  const { track } = useAnalytics()
  const blockContext = useContext(BlockContext)

  const handleBtnAnalytics = (label: string | undefined, link: string) => {
    track({
      event: {
        name: 'component_click',
        data: {}
      } as SPEvent,
      contexts: [
        {
          name: 'component',
          data: {
            component_text: label,
            component_url: link,
          },
        },
        blockContext,
      ] as SPContext[],
    })
  }

  return (
    <Block
      backgroundColor={backgroundColor}
      buttonPrimary={(button1Link || button1AnchorOrExternalLink) && button1Label ? {
        href: btnLink1,
        label: button1Label,
        onClick: () => handleBtnAnalytics(button1Label, btnLink1.route),
        rel: btnLink1.isInternal ? undefined : 'noopener noreferrer',
        target: btnLink1.isInternal ? undefined : '_blank',
      } : undefined }
      buttonSecondary={(button2Link || button2AnchorOrExternalLink) && button2Label ? {
        href: btnLink2,
        label: button2Label,
        onClick: () => handleBtnAnalytics(button2Label, btnLink2.route),
        rel: btnLink2.isInternal ? undefined : 'noopener noreferrer',
        target: btnLink2.isInternal ? undefined : '_blank',
      } : undefined }
      className="duplex-block"
      description={
        <>{richTextRender(description)}</>
      }
      heading={heading}
      id={typeof sectionLink === 'undefined' ? undefined : slugify(sectionLink)}
      paddingBottom={pyMap[paddingBottom]}
      paddingTop={pyMap[paddingTop]}
      preHeading={preheading}
      reverse={imagePosition === 'left' ? true : false}
      slots={{
        image: NextImage
      }}
      slotProps={{
        image: imageFields && {
          alt: imageFields.title,
          className: 'aspect-auto w-screen',
          height: imageDimensions?.height,
          sizes: getImageSizes('100vw', '100vw', '100vw', '50vw', '50vw', '50vw'),
          src: imageFields.file.url,
          width: imageDimensions?.width,
        }
      }}>
    </Block>
  )
}

export default DuplexImageBlock
